import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, deleteDoc, doc } from "firebase/firestore";
import { Plus, Search, Filter, Trash2, Download, TrendingUp, TrendingDown, ArrowLeft, Edit2 } from 'lucide-react';
import { TransactionModal } from './AccountsModals.js';
import { Link } from 'react-router-dom';
import Spinner from './Spinner.js';

const TransactionLedger = ({ db }) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null); // Declare state here


    const fetchData = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "accounts_transactions"), orderBy("date", "desc"));
            const snapshot = await getDocs(q);
            setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [db]);

    const filteredTransactions = transactions.filter(t => {
        const matchesSearch = t.category.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             (t.remark || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === 'all' || t.type === typeFilter;
        return matchesSearch && matchesType;
    });

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this transaction?")) return;
        try {
            await deleteDoc(doc(db, "accounts_transactions", id));
            fetchData();
        } catch (e) {
            alert("Error deleting transaction");
        }
    };

    if (loading) return <Spinner />;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <Link to="/accounts" style={{ color: '#64748b' }}><ArrowLeft size={20}/></Link>
                    <h2 style={{ margin: 0 }}>Transaction Ledger</h2>
                </div>
                {/* Add Transaction button moved to Layout.js */}
            </div>

            {/* Filters */}
            <div style={{ 
                display: 'flex', gap: '15px', marginBottom: '25px', 
                backgroundColor: 'white', padding: '15px', borderRadius: '10px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}>
                <div style={{ flex: 1, position: 'relative' }}>
                    <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
                    <input 
                        placeholder="Search categories or remarks..." 
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{ 
                            width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', 
                            border: '1px solid #e2e8f0', outline: 'none' 
                        }}
                    />
                </div>
                <select 
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white' }}
                >
                    <option value="all">All Types</option>
                    <option value="revenue">Revenue Only</option>
                    <option value="expense">Expenses Only</option>
                </select>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <tr>
                            <th style={thStyle}>Date</th>
                            <th style={thStyle}>Type</th>
                            <th style={thStyle}>Entity Name</th> {/* New column */}
                            <th style={thStyle}>Category</th>
                            <th style={thStyle}>Remark</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                            <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredTransactions.map(t => (
                            <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={tdStyle}>{new Date(t.date).toLocaleDateString()}</td>
                                <td style={tdStyle}>
                                    <span style={{ 
                                        padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase',
                                        backgroundColor: t.type === 'revenue' ? '#dcfce7' : '#fee2e2',
                                        color: t.type === 'revenue' ? '#166534' : '#991b1b'
                                    }}>
                                        {t.type}
                                    </span>
                                </td>
                                <td style={{ ...tdStyle, fontWeight: '600' }}>{t.entityName || 'N/A'}</td> {/* Display Entity Name */}
                                <td style={{ ...tdStyle, fontWeight: '600' }}>{t.category}</td>
                                <td style={{ ...tdStyle, color: '#64748b', fontSize: '12px' }}>{t.remark || '—'}</td>
                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: t.type === 'revenue' ? '#059669' : '#dc2626' }}>
                                    ₹{t.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                        <button 
                                            onClick={() => { setSelectedTransaction(t); setShowModal(true); }}
                                            style={{ 
                                                padding: '6px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', 
                                                backgroundColor: 'white', cursor: 'pointer' 
                                            }}
                                            title="Edit Transaction"
                                        >
                                            <Edit2 size={12} color="#64748b" />
                                        </button>
                                        <button onClick={() => handleDelete(t.id)} 
                                            style={{ 
                                                padding: '6px 8px', borderRadius: '6px', border: '1px solid #fee2e2', 
                                                backgroundColor: 'white', cursor: 'pointer' 
                                            }}
                                            title="Delete Transaction"
                                        >
                                            <Trash2 size={12} color="#ef4444" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredTransactions.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No transactions found matching your filters.</div>
                )}
            </div>

            <TransactionModal 
                show={showModal} 
                onClose={() => { setShowModal(false); setSelectedTransaction(null); }} 
                onSave={fetchData} 
                db={db} 
                transactionData={selectedTransaction}
            />
        </div>
    );
};

const thStyle = { padding: '15px 20px', textAlign: 'left', fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' };
const tdStyle = { padding: '15px 20px', fontSize: '14px', verticalAlign: 'middle' }; // Added verticalAlign for consistency

export default TransactionLedger;
